/**
 * Pure view logic for the Billing page — kept out of the component so it can
 * be unit-tested without a DOM (same pattern as templateVariables.js).
 */

export const MAX_QUANTITY = 500;

// Server /api/billing/status response -> view model for BillingPage.
// Modes: 'disabled' (billing not configured), 'free', 'business'.
export function deriveBillingView(status) {
  if (!status || status.enabled === false) {
    return { mode: 'disabled', tier: 'free' };
  }
  if (status.tier === 'business' && status.subscription) {
    const sub = status.subscription;
    return {
      mode: 'business',
      tier: 'business',
      quantity: sub.quantity || 1,
      status: sub.status,
      // What they actually pay in, so display never contradicts the invoice.
      currency: currencyFromItemPriceId(sub.item_price_id),
      // non_renewing = cancelled but paid up: entitled until the term ends.
      endsInsteadOfRenews: sub.status === 'non_renewing',
      termEnd: formatTermEnd(sub.current_term_end),
    };
  }
  return { mode: 'free', tier: 'free' };
}

// 'business-AUD-monthly' -> 'AUD'; null when the id doesn't follow the convention.
export function currencyFromItemPriceId(id) {
  const m = /^business-([A-Z]{3})-monthly$/.exec(String(id || ''));
  return m ? m[1] : null;
}

// Clamp a quantity input to a valid screen count (default 1).
export function clampQuantity(value) {
  const n = parseInt(value, 10);
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.min(n, MAX_QUANTITY);
}

// ISO date/timestamp -> "18 August 2026" (en-GB long date), or ''.
export function formatTermEnd(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

// Sensible default screen count to prefill checkout with.
export function defaultQuantity(devices) {
  const paired = Array.isArray(devices) ? devices.length : 0;
  return clampQuantity(paired || 1);
}

// Can this subscription still be cancelled? Only an actually-renewing one:
// a `non_renewing` subscription is already cancelled and merely running out
// its paid term, so offering "Cancel" again would be misleading.
export function canCancel(view) {
  return !!view && view.mode === 'business' && !view.endsInsteadOfRenews;
}


